import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  importWhatsAppTemplatesFromMeta,
  listWhatsAppTemplates,
  WhatsAppTemplate,
} from '../services/api';
import SendTemplateDialog from './SendTemplateDialog';
import WhatsAppTemplateRow from './WhatsAppTemplateRow';

interface WhatsAppTemplateListProps {
  onBack: () => void;
  onCreate: () => void;
  onEdit: (template: WhatsAppTemplate) => void;
}

export default function WhatsAppTemplateList({ onBack, onCreate, onEdit }: WhatsAppTemplateListProps) {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<WhatsAppTemplate | null>(null);

  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const ROWS_PER_PAGE_OPTIONS = [10, 15, 20];

  // Case-insensitive substring match on the template name. Trimmed so trailing
  // whitespace from the input doesn't accidentally hide everything.
  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((t) => (t.name || '').toLowerCase().includes(query));
  }, [templates, search]);

  // Reset to the first page whenever the filter changes so the user doesn't
  // land on an empty page when their query shrinks the result set.
  useEffect(() => {
    setPage(0);
  }, [search]);

  const pageStart = page * rowsPerPage;
  const visibleTemplates = filteredTemplates.slice(pageStart, pageStart + rowsPerPage);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listWhatsAppTemplates();
      setTemplates(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSendClick = (template: WhatsAppTemplate) => {
    setActiveTemplate(template);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleImportFromMeta = async () => {
    setImporting(true);
    setImportMessage(null);
    try {
      const result = await importWhatsAppTemplatesFromMeta();
      setImportMessage({
        severity: 'success',
        text:
          `Imported ${result.inserted} new template${result.inserted === 1 ? '' : 's'}` +
          ` (skipped ${result.skipped} already in DB, fetched ${result.fetched} from Meta).`,
      });
      if (result.inserted > 0) {
        await load();
      }
    } catch (err) {
      setImportMessage({
        severity: 'error',
        text: err instanceof Error ? err.message : 'Failed to import templates from Meta.',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to methods
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 32 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              WhatsApp templates
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage approved templates and broadcast them to recipients.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Pull every template registered at Meta and insert any that are missing locally.">
            <span>
              <Button
                variant="outlined"
                startIcon={
                  importing ? <CircularProgress size={16} /> : <CloudDownloadIcon />
                }
                onClick={handleImportFromMeta}
                disabled={importing}
                sx={{ textTransform: 'none' }}
              >
                {importing ? 'Importing…' : 'Import from Meta'}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreate}
            sx={{ textTransform: 'none', bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' } }}
          >
            Create template
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Search by template name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ fontWeight: 700 }}>Template name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created at</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last modified</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No templates yet. Click <strong>Create template</strong> to add one.
                  </TableCell>
                </TableRow>
              )}
              {!loading && templates.length > 0 && filteredTemplates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No templates match <strong>{search}</strong>.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                visibleTemplates.map((template) => (
                  <WhatsAppTemplateRow
                    key={template.id}
                    template={template}
                    onSend={handleSendClick}
                    onEdit={onEdit}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && filteredTemplates.length > ROWS_PER_PAGE_OPTIONS[0] && (
          <TablePagination
            component="div"
            count={filteredTemplates.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        )}
      </Paper>

      <SendTemplateDialog
        open={dialogOpen}
        template={activeTemplate}
        onClose={handleDialogClose}
        onSent={() => {
          // Refresh statuses after a successful schedule.
          load();
        }}
      />

      <Snackbar
        open={!!importMessage}
        autoHideDuration={6000}
        onClose={() => setImportMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {importMessage ? (
          <Alert
            severity={importMessage.severity}
            variant="filled"
            onClose={() => setImportMessage(null)}
            sx={{ width: '100%' }}
          >
            {importMessage.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
